import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 2 + 0.5,
  delay: Math.random() * 4,
  duration: Math.random() * 6 + 6,
}));

export default function CasesComingSoonPage() {
  useSEO({
    title: '変化の記録 — 準備中 | メセナインターコム株式会社',
    description: '変化の記録ページは現在準備中です。近日公開予定。',
    keywords: '変化の記録,制作実績,準備中',
    ogTitle: '変化の記録 — 準備中 | メセナインターコム株式会社',
    ogDescription: '変化の記録ページは現在準備中です。近日公開予定。',
    ogUrl: 'https://mecena-intercom.co.jp/cases',
    canonical: 'https://mecena-intercom.co.jp/cases',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [lineProgress, setLineProgress] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (!visible) return;
    let start: number | null = null;
    const duration = 1800;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setLineProgress(progress);
      if (progress < 1) requestAnimationFrame(animate);
    };
    const raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  return (
    <div className="bg-warm-white min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 flex flex-col relative overflow-hidden">
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {PARTICLES.map((p) => (
            <span
              key={p.id}
              className="absolute rounded-full bg-accent-teal/20"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animation: `floatParticle ${p.duration}s ${p.delay}s ease-in-out infinite alternate`,
              }}
            />
          ))}
        </div>

        {/* Decorative horizontal rule — animated */}
        <div className="absolute top-0 left-0 right-0 h-px bg-divider overflow-hidden" aria-hidden="true">
          <div
            className="h-full bg-accent-teal transition-none"
            style={{ width: `${lineProgress * 100}%`, transition: 'width 0.05s linear' }}
          />
        </div>

        {/* Main content */}
        <div
          ref={containerRef}
          className="flex-1 flex flex-col items-center justify-center px-6 py-32 md:py-48 relative z-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
          }}
        >
          {/* Label */}
          <div className="flex items-center gap-4 mb-12">
            <span className="block w-6 h-px bg-accent-teal" />
            <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Works — Coming Soon</span>
            <span className="block w-6 h-px bg-accent-teal" />
          </div>

          {/* Title */}
          <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-6xl text-center mb-6">
            変化の記録
          </h1>

          {/* Subtitle */}
          <p className="font-garamond italic text-muted-brown text-lg md:text-xl text-center mb-16 tracking-wide">
            Documenting the moments of transformation.
          </p>

          {/* Animated progress bar */}
          <div className="w-48 md:w-64 mb-16">
            <div className="flex justify-between items-center mb-2">
              <span className="font-sans-jp text-[10px] text-muted-light tracking-widest-jp">準備中</span>
              <span className="font-garamond text-xs text-accent-teal italic">In progress</span>
            </div>
            <div className="h-px bg-divider relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-accent-teal" style={{ animation: 'progressPulse 3s ease-in-out infinite' }} />
            </div>
          </div>

          {/* Description */}
          <div className="max-w-lg text-center mb-20">
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp mb-6">
              医療・福祉・教育・地域・文化の現場で積み重ねてきた、<br className="hidden md:block" />
              意味の設計と価値の創造の記録を、まもなく公開します。
            </p>
            <p className="font-sans-jp text-xs text-muted-light leading-relaxed-jp">
              それぞれの組織が社会と出会い直す瞬間を、丁寧に記録してきました。<br className="hidden md:block" />
              その物語を、ここに届けるための準備を進めています。
            </p>
          </div>

          {/* Decorative number grid — teaser */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-20 w-full max-w-xl">
            {['01', '02', '03', '04', '05'].map((num, i) => (
              <div
                key={num}
                className="border border-divider aspect-square flex items-center justify-center relative overflow-hidden group"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: `opacity 0.6s ease ${0.4 + i * 0.12}s`,
                }}
              >
                <span className="font-garamond text-2xl font-light text-divider group-hover:text-accent-teal transition-colors duration-500">
                  {num}
                </span>
                {/* shimmer overlay */}
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-accent-teal/5 to-transparent -translate-x-full"
                  style={{ animation: `shimmer 3s ${i * 0.5}s ease-in-out infinite` }}
                />
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              まずは相談してみる
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-sans-jp text-muted-brown hover:text-accent-teal transition-colors duration-200 whitespace-nowrap cursor-pointer group"
            >
              <span className="inline-block transition-transform duration-200 group-hover:-translate-x-1">←</span>
              トップページへ
            </Link>
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-divider" aria-hidden="true" />
      </main>

      <Footer />
    </div>
  );
}
